import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { taskService } from '../api/taskService';
import { X, Plus, Trash2 } from 'lucide-react';
import { DIVISIONS } from '../constants/divisions';
import CustomTimePicker from './CustomTimePicker';
import { facultyService } from '../api/facultyService';
import FacultyForm from './FacultyForm';
import Select from 'react-select';

const CATEGORIES = {
    'Academic Events': ['Conference', 'Symposium', 'Seminar', 'Workshop'],
    'Engagements': ['Visit', 'Meeting'],
    'Other': ['Annual Day', 'Festive', 'Other']
};

const EventForm = ({ onSubmit, onCancel, initialData, user, users = [] }) => {
    const isDG = user?.roles?.some(r => r === 'DG' || r === 'DG_OFFICE');
    const isCoordinator = user?.roles?.some(r => r.startsWith('COORDINATOR_'));

    const [formData, setFormData] = useState(() => {
        const state = initialData ? { ...initialData } : {
            title: '',
            description: '',
            startDateTime: '',
            endDateTime: '',
            category: 'Academic Events',
            subType: 'Conference',
            eventScope: 'Internal',
            mode: 'In-Person',
            location: '',
            chiefGuest: '',
            hostOrganisation: '',
            externalOrganisation: '',
            linkedTaskIds: [],
            priority: 'Normal',
            status: 'Scheduled',
            scope: 'Individual', // Division assignment scope
            division: '',
            showOnCalendar: null, // Force user to choose
            panelists: []
        };

        const parseDT = (dt) => {
            if (!dt || !dt.includes('T')) return { date: '', time: '' };
            const [d, t] = dt.split('T');
            return { date: d, time: t.substring(0, 5) };
        };

        const start = parseDT(state.startDateTime);
        const end = parseDT(state.endDateTime);

        return {
            ...state,
            startDate: start.date,
            startTime: start.time,
            endDate: end.date,
            endTime: end.time,
            panelists: state.panelists || state.Panelists || []
        };
    });

    const [attachmentsFiles, setAttachmentsFiles] = useState([]);
    const [attachmentsCategory, setAttachmentsCategory] = useState('CONCEPT_AGENDA');
    const [loading, setLoading] = useState(false);
    const [taskSearchQuery, setTaskSearchQuery] = useState('');

    const [showAddExpertModal, setShowAddExpertModal] = useState(false);
    const [activePanelistIndex, setActivePanelistIndex] = useState(null);

    // Fetch external experts
    const { data: faculty = [], refetch: refetchFaculty } = useQuery({
        queryKey: ['faculty'],
        queryFn: async () => {
            const data = await facultyService.getAllFaculty();
            return data || [];
        },
        staleTime: 1000 * 60 * 10
    });

    // Fetch tasks for task linking
    const { data: tasks = [] } = useQuery({
        queryKey: ['eventLinkingTasks'],
        queryFn: () => isDG ? taskService.getAllTasks() : taskService.getAll(),
        enabled: true
    });

    const handleAddPanelist = () => {
        setFormData(prev => ({
            ...prev,
            panelists: [
                ...(prev.panelists || []),
                { type: 'internal', id: '', name: '' }
            ]
        }));
    };

    const handleRemovePanelist = (index) => {
        setFormData(prev => ({
            ...prev,
            panelists: (prev.panelists || []).filter((_, i) => i !== index)
        }));
    };

    const handlePanelistChange = (index, field, value) => {
        setFormData(prev => {
            const newList = [...(prev.panelists || [])];
            newList[index] = { ...newList[index], [field]: value };
            
            // Auto-fill name if id changes
            if (field === 'id') {
                if (newList[index].type === 'internal') {
                    const matchedUser = users.find(u => {
                        const uid = u.Username || u.id;
                        return uid === value;
                    });
                    if (matchedUser) {
                        const attributes = matchedUser.Attributes || [];
                        const nameAttr = attributes.find(a => a.Name === 'name')?.Value;
                        newList[index].name = nameAttr || matchedUser.Username || 'Unknown';
                    }
                } else {
                    const matchedExpert = faculty.find(f => f.FacultyID === value);
                    if (matchedExpert) {
                        newList[index].name = matchedExpert.Name || 'Unknown Expert';
                    }
                }
            }
            // Reset ID and Name if type changes
            if (field === 'type') {
                newList[index].id = '';
                newList[index].name = '';
            }

            return { ...prev, panelists: newList };
        });
    };

    const handleCreateExpertSuccess = async () => {
        setShowAddExpertModal(false);
        // Trigger React Query refetch for faculty
        const { data: updatedFaculty } = await refetchFaculty();
        
        // Find the latest added expert by sorting by CreatedAt or getting the last item in the list
        if (updatedFaculty && updatedFaculty.length > 0 && activePanelistIndex !== null) {
            // Sort by CreatedAt to find the latest
            const sorted = [...updatedFaculty].sort((a, b) => {
                const aTime = a.CreatedAt ? new Date(a.CreatedAt).getTime() : 0;
                const bTime = b.CreatedAt ? new Date(b.CreatedAt).getTime() : 0;
                return bTime - aTime;
            });
            const latestExpert = sorted[0];
            if (latestExpert) {
                handlePanelistChange(activePanelistIndex, 'id', latestExpert.FacultyID);
            }
        }
        setActivePanelistIndex(null);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        setFormData(prev => {
            const next = { ...prev, [name]: type === 'checkbox' ? checked : value };

            // Cascading dropdown logic
            if (name === 'category') {
                next.subType = CATEGORIES[value][0];
            }

            return next;
        });
    };

    const handleTaskToggle = (taskId) => {
        setFormData(prev => {
            const current = prev.linkedTaskIds || [];
            if (current.includes(taskId)) {
                return { ...prev, linkedTaskIds: current.filter(id => id !== taskId) };
            } else {
                return { ...prev, linkedTaskIds: [...current, taskId] };
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        if (formData.showOnCalendar === null) {
            alert('Please select whether to show this event on the calendar.');
            setLoading(false);
            return;
        }

        try {
            const submitData = { ...formData };
            
            // Reconstruct ISO strings for backend
            if (submitData.startDate && submitData.startTime) {
                submitData.startDateTime = `${submitData.startDate}T${submitData.startTime}`;
            }
            if (submitData.endDate && submitData.endTime) {
                submitData.endDateTime = `${submitData.endDate}T${submitData.endTime}`;
            }
            
            // Clean up temporary UI fields
            delete submitData.startDate;
            delete submitData.startTime;
            delete submitData.endDate;
            delete submitData.endTime;

            submitData.showOnCalendar = submitData.showOnCalendar === 'yes';

            // Map to capitalized Panelists for backend & app consistency
            submitData.Panelists = submitData.panelists || [];
            delete submitData.panelists;

            await onSubmit({ ...submitData, attachmentsFiles, attachmentsCategory });
        } finally {
            setLoading(false);
        }
    };

    const getPriorityColor = (p) => {
        switch (p) {
            case 'High': return '#dc3545';
            case 'Low': return '#28a745';
            default: return '#333';
        }
    };

    return (
        <form onSubmit={handleSubmit} style={{
            display: 'flex', flexDirection: 'column', gap: '1.5rem', maxWidth: '900px',
            background: 'linear-gradient(155deg, #dbeafe 0%, #ede9fe 42%, #ccfbf1 100%)', // Match Sidebar gradient
            padding: '2.5rem', borderRadius: '16px', border: '1px solid #c7d2fe',
            boxShadow: '0 12px 35px rgba(30, 41, 59, 0.1)',
            color: '#1e293b'
        }}>
            <div style={{ paddingBottom: '1rem', borderBottom: '1px solid rgba(0,0,0,0.05)', marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <h3 style={{ margin: '0 0 0.25rem 0', color: '#1e3a8a', fontSize: '1.4rem', fontWeight: 700 }}>{initialData ? 'Edit Event' : 'Create New Event'}</h3>
                    <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b' }}>Provide the details below to organize your event.</p>
                </div>
                <button
                    type="button"
                    onClick={onCancel}
                    style={{
                        background: 'rgba(255, 255, 255, 0.5)',
                        border: 'none',
                        borderRadius: '50%',
                        padding: '0.5rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background 0.2s',
                        color: '#64748b'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.8)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.5)'}
                >
                    <X size={20} />
                </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '1.5rem', alignItems: 'start' }}>
                <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.04)', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>Event Title</label>
                    <input
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        required
                        placeholder="Enter event title"
                        style={{ width: '100%', padding: '0.7rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', transition: 'border-color 0.2s' }}
                    />

                    <label style={{ display: 'block', margin: '1.5rem 0 0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>Description (optional)</label>
                    <textarea
                        name="description"
                        value={formData.description || ''}
                        onChange={handleChange}
                        placeholder="Add event description"
                        rows={4}
                        style={{ width: '100%', padding: '0.7rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', resize: 'vertical', transition: 'border-color 0.2s' }}
                    />
                </div>

                {/* Scope Selection */}
                <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.04)', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>Access Scope</label>
                    <select
                        name="scope"
                        value={formData.scope}
                        onChange={handleChange}
                        style={{ width: '100%', padding: '0.7rem', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f8fafc' }}
                        disabled={!isCoordinator && !isDG}
                    >
                        <option value="Individual">Individual</option>
                        {(isCoordinator || isDG) && <option value="Division">Division</option>}
                        {isDG && <option value="All Divisions">All Divisions</option>}
                    </select>

                    {/* DG Division Selector */}
                    {isDG && formData.scope === 'Division' && (
                        <div style={{ marginTop: '1.5rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>Target Division</label>
                            <select
                                name="division"
                                value={formData.division || ''}
                                onChange={handleChange}
                                required
                                style={{ width: '100%', padding: '0.7rem', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f8fafc' }}
                            >
                                <option value="">Select Target Division...</option>
                                {Array.from(new Set([
                                    ...DIVISIONS.map(d => d.id),
                                    ...users.map(u => {
                                        const div = u.Division || u.Attributes?.find(a => a.Name === 'custom:division')?.Value;
                                        return div ? div.toUpperCase() : null;
                                    }).filter(d => Boolean(d) && d !== 'N/A')
                                ])).sort().map(divId => {
                                    const divObj = DIVISIONS.find(d => d.id === divId);
                                    return <option key={divId} value={divId}>{divObj ? divObj.name : divId}</option>;
                                })}
                            </select>
                        </div>
                    )}
                </div>
            </div>

            {/* NEW CATEGORIZATION */}
            <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.04)', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.75rem' }}>
                    <h4 style={{ margin: 0, color: '#1e293b', fontSize: '1rem', fontWeight: 600 }}>Classification</h4>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem' }}>
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>Category</label>
                        <select
                            name="category"
                            value={formData.category}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '0.7rem', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f8fafc' }}
                        >
                            {Object.keys(CATEGORIES).map(cat => <option key={cat} value={cat}>{cat}</option>)}
                        </select>
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>Sub Type</label>
                        <select
                            name="subType"
                            value={formData.subType}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '0.7rem', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f8fafc' }}
                        >
                            {CATEGORIES[formData.category].map(sub => <option key={sub} value={sub}>{sub}</option>)}
                        </select>
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>Internal / External</label>
                        <select
                            name="eventScope"
                            value={formData.eventScope}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '0.7rem', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f8fafc' }}
                        >
                            <option value="Internal">Internal</option>
                            <option value="External">External</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* NEW METADATA FIELDS */}
            <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.04)', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.75rem' }}>
                    <h4 style={{ margin: 0, color: '#1e293b', fontSize: '1rem', fontWeight: 600 }}>Venue & Hosting</h4>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem' }}>
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>Mode</label>
                        <select
                            name="mode"
                            value={formData.mode}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '0.7rem', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f8fafc' }}
                        >
                            <option value="In-Person">In-Person</option>
                            <option value="Online">Online</option>
                            <option value="Hybrid">Hybrid</option>
                        </select>
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>Location</label>
                        <input
                            name="location"
                            value={formData.location}
                            onChange={handleChange}
                            placeholder="e.g. Hall A"
                            style={{ width: '100%', padding: '0.7rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', transition: 'border-color 0.2s' }}
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>Host Org</label>
                        <input
                            name="hostOrganisation"
                            value={formData.hostOrganisation}
                            onChange={handleChange}
                            placeholder="Host Organization"
                            style={{ width: '100%', padding: '0.7rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', transition: 'border-color 0.2s' }}
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>External Org</label>
                        <input
                            name="externalOrganisation"
                            value={formData.externalOrganisation}
                            onChange={handleChange}
                            placeholder="External Organization"
                            style={{ width: '100%', padding: '0.7rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', transition: 'border-color 0.2s' }}
                        />
                    </div>

                    {/* Second row in Venue & Hosting aligned with first row */}
                    <div style={{ gridColumn: 'span 4' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>Chief Guest</label>
                        <input
                            name="chiefGuest"
                            value={formData.chiefGuest}
                            onChange={handleChange}
                            placeholder="Name of Chief Guest (Optional)"
                            style={{ width: '100%', padding: '0.7rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', transition: 'border-color 0.2s' }}
                        />
                    </div>
                    <div style={{ gridColumn: 'span 2' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>Start Date & Time</label>
                        <div style={{ display: 'flex', gap: '1rem' }}>
                            <input
                                type="date"
                                name="startDate"
                                value={formData.startDate || ''}
                                onChange={handleChange}
                                required
                                style={{ flex: 1, padding: '0.7rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontFamily: 'inherit', background: '#f8fafc' }}
                            />
                            <CustomTimePicker
                                name="startTime"
                                value={formData.startTime || ''}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
                    <div style={{ gridColumn: 'span 2' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>End Date & Time</label>
                        <div style={{ display: 'flex', gap: '1rem' }}>
                            <input
                                type="date"
                                name="endDate"
                                value={formData.endDate || ''}
                                onChange={handleChange}
                                required
                                style={{ flex: 1, padding: '0.7rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontFamily: 'inherit', background: '#f8fafc' }}
                            />
                            <CustomTimePicker
                                name="endTime"
                                value={formData.endTime || ''}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Event Panelists Section */}
            <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.04)', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.75rem' }}>
                    <div>
                        <h4 style={{ margin: 0, color: '#1e293b', fontSize: '1rem', fontWeight: 600 }}>Event Panelists</h4>
                        <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem', color: '#64748b' }}>Select academic experts and internal team members to join as panelists.</p>
                    </div>
                    <button
                        type="button"
                        onClick={handleAddPanelist}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '0.35rem', background: '#e0f2fe', color: '#0369a1',
                            padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #bae6fd', fontWeight: 600,
                            fontSize: '0.85rem', cursor: 'pointer', transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#bae6fd'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = '#e0f2fe'; }}
                    >
                        <Plus size={16} /> Add Panelist
                    </button>
                </div>

                {(!formData.panelists || formData.panelists.length === 0) ? (
                    <div style={{ textAlign: 'center', padding: '2rem 1rem', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1', color: '#64748b', fontSize: '0.85rem' }}>
                        No panelists added yet. Click "Add Panelist" to start assigning expert contributors.
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {formData.panelists.map((panelist, index) => {
                            const internalOptions = users.map(u => {
                                const uid = u.Username || u.id;
                                const nameAttr = u.Attributes?.find(a => a.Name === 'name')?.Value || u.Username || 'Unknown';
                                const divAttr = u.Attributes?.find(a => a.Name === 'custom:division')?.Value || u.Division || '';
                                return {
                                    value: uid,
                                    label: `${nameAttr} ${divAttr ? `(${divAttr.toUpperCase()})` : ''}`
                                };
                            });

                            const externalOptions = faculty.map(f => ({
                                value: f.FacultyID,
                                label: `${f.Name} ${f.Organisation ? `— ${f.Organisation}` : ''}`
                            }));

                            const currentOptions = panelist.type === 'internal' ? internalOptions : externalOptions;
                            const selectedOption = currentOptions.find(opt => opt.value === panelist.id) || null;

                            return (
                                <div key={index} style={{ display: 'flex', gap: '1rem', alignItems: 'center', background: '#f8fafc', padding: '1rem', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                    <div style={{ flex: '0 0 160px' }}>
                                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: '0.35rem' }}>Expert Type</label>
                                        <select
                                            value={panelist.type}
                                            onChange={(e) => handlePanelistChange(index, 'type', e.target.value)}
                                            style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1', background: 'white', fontSize: '0.85rem', height: '38px' }}
                                        >
                                            <option value="internal">RIS Staff (Internal)</option>
                                            <option value="external">External Expert</option>
                                        </select>
                                    </div>

                                    <div style={{ flex: 1 }}>
                                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: '0.35rem' }}>Select Expert Profile</label>
                                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                            <div style={{ flex: 1 }}>
                                                <Select
                                                    options={currentOptions}
                                                    value={selectedOption}
                                                    onChange={(option) => handlePanelistChange(index, 'id', option ? option.value : '')}
                                                    placeholder="Search expert..."
                                                    isClearable
                                                    styles={{
                                                        control: (base) => ({
                                                            ...base,
                                                            minHeight: '38px',
                                                            borderRadius: '6px',
                                                            borderColor: '#cbd5e1',
                                                            fontSize: '0.85rem',
                                                            background: 'white',
                                                            boxShadow: 'none',
                                                            '&:hover': {
                                                                borderColor: '#cbd5e1'
                                                            }
                                                        }),
                                                        menu: (base) => ({
                                                            ...base,
                                                            zIndex: 9999
                                                        })
                                                    }}
                                                />
                                            </div>

                                            {panelist.type === 'external' && (
                                                <button
                                                    type="button"
                                                    title="Register new expert profile in database"
                                                    onClick={() => {
                                                        setActivePanelistIndex(index);
                                                        setShowAddExpertModal(true);
                                                    }}
                                                    style={{
                                                        background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#047857',
                                                        padding: '0.5rem', borderRadius: '6px', display: 'flex', alignItems: 'center',
                                                        justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s',
                                                        height: '38px', width: '38px'
                                                    }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.background = '#d1fae5'; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.background = '#ecfdf5'; }}
                                                >
                                                    <Plus size={16} />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => handleRemovePanelist(index)}
                                        style={{
                                            alignSelf: 'flex-end', background: '#fef2f2', border: '1px solid #fee2e2',
                                            color: '#ef4444', padding: '0.5rem', borderRadius: '6px', display: 'flex',
                                            alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s',
                                            height: '38px', width: '38px'
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.background = '#fef2f2'; }}
                                        title="Remove panelist"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* FacultyForm Modal (Option A) */}
            {showAddExpertModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(8px)',
                    zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem'
                }}>
                    <FacultyForm
                        initialData={null}
                        onSuccess={handleCreateExpertSuccess}
                        onCancel={() => {
                            setShowAddExpertModal(false);
                            setActivePanelistIndex(null);
                        }}
                    />
                </div>
            )}

            {/* Calendar Settings */}
            <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.04)', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>
                    Show this Event on the Calendar? <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.5rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                            type="radio"
                            name="showOnCalendar"
                            value="yes"
                            checked={formData.showOnCalendar === 'yes'}
                            onChange={handleChange}
                            style={{ width: '1.1rem', height: '1.1rem', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.95rem', color: '#1e293b' }}>Yes, show on calendar</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                            type="radio"
                            name="showOnCalendar"
                            value="no"
                            checked={formData.showOnCalendar === 'no'}
                            onChange={handleChange}
                            style={{ width: '1.1rem', height: '1.1rem', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.95rem', color: '#1e293b' }}>No, hide from calendar</span>
                    </label>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                {/* Task Linking */}
                <div style={{ background: 'white', border: '1px solid rgba(0,0,0,0.04)', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                    <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem', fontWeight: 600, color: '#1e293b' }}>
                        <span>Link to Tasks</span>
                        <div style={{
                            background: '#f1f5f9', padding: '0.1rem 0.6rem', borderRadius: '99px', fontSize: '0.75rem', fontWeight: 700, color: '#475569'
                        }}>
                            {formData.linkedTaskIds?.length || 0} Selected
                        </div>
                    </label>
                    <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '1rem', marginTop: 0 }}>Select related tasks to attach to this event.</p>

                    <input
                        type="text"
                        placeholder="Search tasks..."
                        value={taskSearchQuery}
                        onChange={(e) => setTaskSearchQuery(e.target.value)}
                        style={{ width: '100%', padding: '0.6rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', marginBottom: '0.75rem', background: '#f8fafc' }}
                    />

                    <div style={{ height: '180px', overflowY: 'auto', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.5rem' }}>
                        {tasks.length === 0 ? (
                            <div style={{ padding: '1rem', fontSize: '0.85rem', color: '#94a3b8', fontStyle: 'italic', textAlign: 'center' }}>No available tasks.</div>
                        ) : (
                            tasks.filter(t => t.Title?.toLowerCase().includes(taskSearchQuery.toLowerCase())).map(task => (
                                <label key={task.TaskID || task.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem', cursor: 'pointer', borderRadius: '6px', ':hover': { background: '#e2e8f0' } }}>
                                    <input
                                        type="checkbox"
                                        checked={(formData.linkedTaskIds || []).includes(task.TaskID || task.id)}
                                        onChange={() => handleTaskToggle(task.TaskID || task.id)}
                                        style={{ cursor: 'pointer', width: '1.1rem', height: '1.1rem', accentColor: '#3b82f6' }}
                                    />
                                    <span style={{ fontSize: '0.9rem', color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {task.Title}
                                    </span>
                                </label>
                            ))
                        )}
                    </div>
                </div>

                {/* Attachments & Priority */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <div style={{ background: 'white', border: '1px solid rgba(0,0,0,0.04)', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#1e293b' }}>Priority Level</label>
                        <select
                            name="priority"
                            value={formData.priority}
                            onChange={handleChange}
                            style={{
                                width: '100%', padding: '0.7rem', borderRadius: '8px',
                                border: '1px solid #cbd5e1',
                                background: '#f8fafc',
                                color: getPriorityColor(formData.priority),
                                fontWeight: 600
                            }}
                        >
                            <option value="Normal" style={{ color: '#334155' }}>Normal</option>
                            <option value="High" style={{ color: '#ef4444' }}>High</option>
                            <option value="Low" style={{ color: '#22c55e' }}>Low</option>
                        </select>
                    </div>

                    <div style={{ flex: 1, background: 'white', border: '1px solid rgba(0,0,0,0.04)', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#1e293b' }}>
                            Attach Files <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: '0.8rem' }}>(optional)</span>
                        </label>
                        <select
                            value={attachmentsCategory}
                            onChange={(e) => setAttachmentsCategory(e.target.value)}
                            style={{ width: '100%', marginBottom: '1rem', padding: '0.7rem', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f8fafc' }}
                        >
                            <option value="CONCEPT_AGENDA">Concept/Agenda</option>
                            <option value="TRANSCRIPT">Transcript</option>
                            <option value="FINAL_REPORT_SUMMARY">Final Report Summary</option>
                        </select>
                        <input
                            type="file"
                            multiple
                            onChange={(e) => setAttachmentsFiles(Array.from(e.target.files || []))}
                            style={{
                                width: '100%', fontSize: '0.85rem', color: '#475569',
                                fileSelectorButton: { padding: '0.5rem 1rem', borderRadius: '6px', background: '#e2e8f0', border: 'none', cursor: 'pointer', fontWeight: 600, color: '#1e293b' }
                            }}
                        />
                        {attachmentsFiles.length > 0 && (
                            <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#10b981', background: '#ecfdf5', padding: '0.5rem', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600 }}>
                                ✓ {attachmentsFiles.length} file(s) ready
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.5rem' }}>
                <button
                    type="submit"
                    disabled={loading}
                    style={{
                        flex: 1, padding: '0.75rem', background: '#007bff', color: 'white', fontWeight: 600,
                        border: 'none', borderRadius: '8px', cursor: 'pointer', opacity: loading ? 0.7 : 1,
                        boxShadow: '0 4px 6px rgba(0,123,255,0.2)'
                    }}
                >
                    {loading ? 'Saving...' : (initialData ? 'Save Changes' : 'Create Event')}
                </button>
                <button
                    type="button"
                    onClick={onCancel}
                    style={{
                        padding: '0.75rem 2.5rem', background: '#f8f9fa', color: '#495057', fontWeight: 500,
                        border: '1px solid #ced4da', borderRadius: '8px', cursor: 'pointer'
                    }}
                >
                    Cancel
                </button>
            </div>
        </form>
    );
};

export default EventForm;
